import usePrevious from 'hooks/usePrevious';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_DELETE_REQUEST, UPDATE_APPLICATION_DETAIL_POST_DATA } from 'store/key';
import { generateRandomId, produce } from 'utility/general';

const useLeadPITableData = ({ formId = -1, siteInfoId = -1 }) => {
    const [openPopup, setOpenPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const serverTableKey = 'ciDelegation';
    const updatePostData = useSelector((state) => state.applicationDetailPostData.postData[serverTableKey]);
    const remoteData = useSelector((state) => state.applicationDetail?.[formId]?.[serverTableKey]);
    const [rows, setRows] = useState(remoteData ?? []);
    const previousData = usePrevious(remoteData);
    const [isEdit, setEdit] = useState(false);

    // update row when remote data update
    useEffect(() => {
        if (!isEqual(previousData, remoteData)) {
            setRows(remoteData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteData]);

    const dispatch = useDispatch();

    const handleEditClick = (row) => {
        setEdit(true);
        setSelectedRow(row);
        setOpenPopup(true);
    };

    const triggerCreateForm = async (id) => {
        setEdit(false);
        setSelectedRow({});
        setOpenPopup(true);
    };

    const onRecordUpdate = async (data) => {
        setRows(
            produce((draft) => {
                data.id = `temp_${generateRandomId()}`;
                draft.push(data);
            }),
        );
    };

    const handleEditAfterCreation = (row) => {
        let postData = updatePostData ?? [];

        const index = postData.findIndex((item) => {
            return item.id === row.id;
        });

        if (index === -1) {
            postData.push(row);
        } else {
            postData[index] = row;
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: postData,
                applicationFormId: formId,
            },
        });

        //update local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                draft[index] = row;
            }),
        );
    };

    const handleCreateAfterCreation = async (data) => {
        data.id = `temp_${generateRandomId()}`;
        const newRows = produce(rows, (draft) => {
            draft.push(data);
        });
        setRows(newRows);
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...(updatePostData ?? []), data],
                applicationFormId: formId,
            },
        });
    };

    const handleDeleteAfterCreation = async (row) => {
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // skip if id starts with temp_, e.g. not exist in server
        if (row.id.toString().startsWith('temp_')) {
            let postData = updatePostData ?? [];

            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            if (index !== -1) {
                postData.splice(index, 1);
            }

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: postData,
                    applicationFormId: formId,
                },
            });

            return;
        }

        dispatch({
            type: ADD_DELETE_REQUEST,
            payload: {
                type: serverTableKey,
                siteInfoId,
                id: row.id,
                applicationFormId: formId,
            },
        });
    };

    const columns = [
        { field: 'email', headerName: 'Email', width: 180 },
        { field: 'title', headerName: 'Title', width: 130, tooltip: 'e.g. Dr/Mr/Mrs/Ms/Prof.' },
        {
            field: 'surname',
            headerName: 'Surname',
            width: 120,
            valueGetter: (params) => params.getValue('surname'),
        },
        {
            field: 'firstName',
            headerName: 'First name',
            width: 120,
            valueGetter: (params) => params.getValue('firstName'),
        },
        {
            field: 'position',
            headerName: 'Position',
            width: 130,
            tooltip: 'Please fill in the position relevant to the hospital/institution which you have selected.',
        },
        { field: 'cluster', headerName: 'Cluster', width: 100 },
        {
            field: 'institution',
            headerName: 'Hospital / Institution',
            width: 120,
            // tooltip: (
            //     <div>
            //         <div>Central IRB: Coordinating Investigator shall be an employee/appointee of HA</div>
            //         <div style={{ marginTop: 15 }}>
            //             HKU/HA HKW IRB: Coordinating Investigator shall be an employee/appointee/student of the HKU or
            //             HA HKWC
            //         </div>
            //         <div style={{ marginTop: 15 }}>
            //             Joint CUHK-NTEC CREC: Coordinating Investigator shall be an employee/appointee/student of the
            //             CUHK or HK NTEC
            //         </div>
            //     </div>
            // ),
        },
        {
            field: 'department',
            headerName: 'Department',
            width: 120,
        },
    ];

    return {
        columns,
        rows,
        openPopup,
        setOpenPopup,
        selectedRow,
        triggerCreateForm,
        onRecordUpdate,
        handleEditClick,
        setRows,
        handleDeleteAfterCreation,
        handleEditAfterCreation,
        handleCreateAfterCreation,
        isEdit, // onEditSubmit,
    };
};

export default useLeadPITableData;
